import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDropdown } from "./inputs";

interface Props {
	countryDropdownId: string;
	provinceDropdownId: string;

	selectedCountryName: string;
	setSelectedCountryName: React.Dispatch<React.SetStateAction<string>>;
	selectedState: string;
	setSelectedState: React.Dispatch<React.SetStateAction<string>>;
	setUnsavedChanges?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface State {
	name: string;
	abbreviation: string;
}

interface Country {
	name: string;
	abbreviation: string;
	phonePrefix: string;
	states: State[];
}

export default function CountryProvinceSelector(props: Props) {
	const [countries, setCountries] = useState<Array<Country>>([]); // Country data
	const [selectedCountry, setSelectedCountry] = useState<Country>();

	const [loading, setLoading] = useState<boolean>(true); // Loading state

	const {
		countryDropdownId,
		provinceDropdownId,
		selectedCountryName,
		setSelectedCountryName,
		selectedState,
		setSelectedState,
		setUnsavedChanges,
	} = props;

	const { t: translation } = useTranslation();

	// Fetch countries and provinces data from the JSON file located in the public folder
	useEffect(() => {
		fetch("/country_and_states.json")
			.then((response) => response.json())
			.then((data: Country[]) => {
				setCountries(data); // Set the countries data
				if (!selectedCountryName) {
					setSelectedCountryName(data[0].name);
					setSelectedCountry(data[0]);
				} else {
					setSelectedCountry(
						data.find((country) => {
							return country.name === selectedCountryName;
						})
					);
				}
				if (!selectedState) {
					setSelectedState(data[0].states[0].name);
				}
				setLoading(false); // Stop loading once data is fetched
			})
			.catch((error) => {
				console.error("Error loading the countries data:", error);
				setLoading(false); // Stop loading in case of an error
			});
	}, []);

	// Handle country selection change
	function handleCountryChange(countryName: string) {
		setSelectedCountryName(countryName);
		if (setUnsavedChanges) {
			setUnsavedChanges(true);
		}
		const country: Country | undefined = countries.find((country) => {
			return country.name === countryName;
		});
		if (country) {
			setSelectedCountry(country);
			setSelectedState(country?.states[0].name || "");
		}
	}

	return (
		<>
			<CustomDropdown
				id={countryDropdownId}
				label={translation("Country")}
				options={countries.map((country: Country) => {
					return {
						title: country.name,
						value: country.name,
					};
				})}
				value={selectedCountryName}
				setValue={handleCountryChange}
			></CustomDropdown>

			<CustomDropdown
				id={provinceDropdownId}
				label={translation("ProvinceOrState")}
				options={
					selectedCountry
						? selectedCountry.states.map((state: State) => {
								return {
									title: state.name,
									value: state.name,
								};
						  })
						: []
				}
				value={selectedState}
				setValue={(state: string) => {
					setSelectedState(state);
					if (setUnsavedChanges) {
						setUnsavedChanges(true);
					}
				}}
			></CustomDropdown>
		</>
	);
}
